import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Icon from '../foundations/Icon';
import css from './FileDropzone.css';

// A click-or-drag file dropzone, mirroring FileField's prop shape.
export default function FileDropzone({
    value = null,
    label = '',
    promptText = 'STATIC.BUTTONS.UPLOAD_FILE',
    clearLabel = 'STATIC.BUTTONS.DELETE_FILE',
    clearable = false,
    disabled = false,
    error = '',
    iconName = 'file-upload',
    acceptableTypes = ['*'],
    onChange,
}) {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);

    const openPicker = () => !disabled && inputRef.current && inputRef.current.click();

    const pick = files => {
        if (!disabled && files && files.length) {
            onChange(files[0]);
        }
    };

    return (
        <div className={css.container}>
            {label && (
                <div className={classNames(css.fieldLabel, { [css.error]: error })}>
                    <Translate id={label} />
                </div>
            )}
            <input
                ref={inputRef}
                type="file"
                // Inline (not a class) so a global input[type=file] rule can't override it.
                style={{ display: 'none' }}
                disabled={disabled}
                accept={acceptableTypes.join(',')}
                onChange={event => {
                    pick(event.target.files);
                    event.target.value = '';
                }}
            />
            {value ? (
                <div className={css.fileChip}>
                    <Icon name="file-type-csv" interactive={false} />
                    <span className={css.fileName}>{value.name}</span>
                    {clearable && (
                        <button
                            type="button"
                            className={css.remove}
                            disabled={disabled}
                            onClick={() => onChange(null)}
                        >
                            <Translate id={clearLabel} />
                        </button>
                    )}
                </div>
            ) : (
                <div
                    className={classNames(css.dropzone, {
                        [css.dragging]: dragging,
                        [css.disabled]: disabled,
                    })}
                    role="button"
                    tabIndex={0}
                    onClick={openPicker}
                    onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && openPicker()}
                    onDragOver={event => {
                        event.preventDefault();
                        !disabled && setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={event => {
                        event.preventDefault();
                        setDragging(false);
                        pick(event.dataTransfer.files);
                    }}
                >
                    <Icon name={iconName} interactive={false} />
                    <span>
                        <Translate id={promptText} />
                    </span>
                </div>
            )}
            {error && <div className={css.errorMessage}>{error}</div>}
        </div>
    );
}

FileDropzone.propTypes = {
    value: PropTypes.instanceOf(File),
    label: PropTypes.string,
    promptText: PropTypes.string,
    clearLabel: PropTypes.string,
    clearable: PropTypes.bool,
    disabled: PropTypes.bool,
    error: PropTypes.string,
    iconName: PropTypes.string,
    acceptableTypes: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func.isRequired,
};
